import React, { useState } from 'react';
import { FileText, ChevronDown, ChevronUp, AlertCircle, CheckCircle, XCircle, Clock, User, Car, CreditCard, Shield, MapPin, Phone, Mail, Download, Share2 } from 'lucide-react';

const TermsOfService = () => {
  const [openSections, setOpenSections] = useState({});
  const [acceptedSections, setAcceptedSections] = useState({});

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const toggleAcceptance = (sectionId) => {
    setAcceptedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      content: `By accessing or using RideHub's cab booking services ("Services"), you agree to be bound by these Terms of Service ("Terms"). These Terms constitute a legally binding agreement between you and RideHub Technologies Inc.

If you do not agree to these Terms, you may not access or use our Services. We reserve the right to modify these Terms at any time, and such modifications shall be effective immediately upon posting.`,
      important: true
    },
    {
      id: 'services',
      title: '2. Services Description',
      content: `RideHub provides an online platform that connects riders with independent third-party transportation providers ("Driver Partners").

**What We Provide:**
- Real-time ride matching and booking
- Payment processing and receipt generation
- Customer support and dispute resolution
- Safety features and ride monitoring

**What We Are Not:**
- We are not a transportation carrier
- We do not employ Driver Partners
- We do not guarantee service availability
- We are not liable for third-party actions`,
      important: false
    },
    {
      id: 'user-accounts',
      title: '3. User Accounts',
      content: `**Eligibility:**
- You must be at least 18 years old to create an account
- You must provide accurate and complete information
- You are responsible for maintaining account security
- You must notify us immediately of any unauthorized use

**Account Restrictions:**
- One account per individual
- No transferring or sharing accounts
- We may suspend accounts for violations
- You may delete your account at any time

**Verification:**
We may require identity verification for security purposes.`,
      important: true
    },
    {
      id: 'booking-payments',
      title: '4. Booking and Payments',
      content: `**Ride Booking:**
- Bookings are requests, not guaranteed services
- Driver Partners may accept or decline requests
- Estimated fares are subject to change
- You must be present at pickup location

**Pricing:**
- Fares are calculated based on distance, time, and demand
- Surge pricing may apply during high demand
- Additional fees may include tolls, waiting time, or cleaning
- You will see the final fare before confirming booking

**Payment Methods:**
- We accept credit/debit cards and digital wallets
- All payments are processed securely
- Refunds are processed to original payment method
- You authorize us to charge the provided payment method`,
      important: true
    },
    {
      id: 'user-conduct',
      title: '5. User Conduct and Responsibilities',
      content: `**As a Rider, You Agree To:**
- Provide accurate pickup and drop-off locations
- Be ready at the pickup location at the scheduled time
- Treat Driver Partners and vehicles with respect
- Wear seatbelts and follow traffic laws
- Not cause damage to vehicles
- Not carry prohibited items

**Prohibited Activities:**
- No smoking in vehicles
- No alcohol consumption by minors
- No illegal substances
- No weapons of any kind
- No unauthorized commercial activities
- No discrimination or harassment`,
      important: true
    },
    {
      id: 'cancellation-refunds',
      title: '6. Cancellation and Refunds',
      content: `**Cancellation Policy:**
- Riders may cancel rides before Driver Partner arrival
- Cancellation fees may apply after certain time
- Repeated cancellations may result in account suspension
- Driver Partners may cancel under certain circumstances

**Refund Policy:**
- Refunds considered for service failures
- Processing time: 5-7 business days
- Disputes must be filed within 7 days
- We reserve final decision on refunds

**No-Show Policy:**
If a rider isn't present within 5 minutes of arrival, the ride may be canceled with applicable charges.`,
      important: false
    },
    {
      id: 'intellectual-property',
      title: '7. Intellectual Property',
      content: `**Our Rights:**
- RideHub owns all platform intellectual property
- This includes software, designs, and branding
- You may not copy, modify, or distribute our IP
- All rights not expressly granted are reserved

**Your Content:**
- You retain rights to your content
- You grant us license to use content for service provision
- We may use feedback to improve our services
- You represent you have rights to shared content`,
      important: false
    },
    {
      id: 'privacy',
      title: '8. Privacy and Data Protection',
      content: `**Data Collection:**
We collect and use information as described in our Privacy Policy, including:
- Personal identification information
- Location and ride data
- Payment information
- Device and usage data

**Data Usage:**
- To provide and improve services
- For safety and security purposes
- For customer support
- As required by law

**Your Rights:**
You have rights regarding your personal data as outlined in our Privacy Policy.`,
      important: true
    },
    {
      id: 'limitation-liability',
      title: '9. Limitation of Liability',
      content: `**Service "As Is":**
Our services are provided "as is" without warranties of any kind. We do not guarantee:
- Uninterrupted or error-free service
- Specific quality of transportation
- Driver Partner actions or behavior
- Travel time estimates

**Liability Cap:**
To the maximum extent permitted by law, our total liability shall not exceed the greater of:
- The amount paid by you for services in the past 6 months
- $500 USD

**Exclusions:**
This limitation does not apply to:
- Personal injury or death caused by negligence
- Fraud or fraudulent misrepresentation
- Any liability that cannot be limited by law`,
      important: true
    },
    {
      id: 'indemnification',
      title: '10. Indemnification',
      content: `You agree to indemnify and hold harmless RideHub and its affiliates from any claims, damages, or expenses arising from:

- Your use of our services
- Your violation of these Terms
- Your violation of any laws or third-party rights
- Your interaction with Driver Partners
- Any content you submit

This includes reasonable attorneys' fees and legal costs. We reserve the right to assume exclusive defense and control of any matter subject to indemnification.`,
      important: true
    },
    {
      id: 'termination',
      title: '11. Termination',
      content: `**By You:**
You may stop using our services and delete your account at any time.

**By Us:**
We may terminate or suspend your access to services immediately if:
- You violate these Terms
- We suspect fraudulent activity
- Required by law or legal process
- For operational reasons

**Effects of Termination:**
- Your right to use services ceases immediately
- Outstanding payments remain due
- Certain provisions survive termination
- We may retain certain data as required by law`,
      important: false
    },
    {
      id: 'dispute-resolution',
      title: '12. Dispute Resolution',
      content: `**Informal Resolution:**
We encourage you to contact us first to resolve disputes informally.

**Arbitration Agreement:**
Most disputes must be resolved through binding arbitration:
- Arbitration is less formal than court
- Arbitration uses neutral arbitrators
- Arbitration is subject to limited review
- You waive right to jury trial and class actions

**Exceptions:**
You may bring claims in small claims court or for injunctive relief.

**Governing Law:**
These Terms are governed by the laws of the State of Delaware, USA.`,
      important: true
    },
    {
      id: 'miscellaneous',
      title: '13. Miscellaneous',
      content: `**Entire Agreement:**
These Terms constitute the entire agreement between you and RideHub.

**Severability:**
If any provision is found invalid, the remainder remains enforceable.

**No Waiver:**
Our failure to enforce any provision doesn't waive our right to enforce it later.

**Assignment:**
You may not assign your rights; we may assign ours.

**Force Majeure:**
We are not liable for delays beyond our reasonable control.`,
      important: false
    },
    {
      id: 'contact-info',
      title: '14. Contact Information',
      content: `For questions about these Terms, please contact us:

**Legal Department:**
RideHub Technologies Inc.
123 Innovation Drive
Tech City, TC 12345
United States

**Email:** myjourneycabs@gmail.com
**Phone:** +1 (555) 123-LEGAL

**Customer Support:**
Email: myjourneycabs@gmail.com
Phone: +1 (555) 123-RIDES

We typically respond to legal inquiries within 3-5 business days.`,
      important: false
    }
  ];

  const lastUpdated = "January 15, 2024";
  const effectiveDate = "January 22, 2024";

  const acceptanceProgress = Object.keys(acceptedSections).filter(key => acceptedSections[key]).length;
  const totalImportantSections = sections.filter(section => section.important).length;
  const acceptancePercentage = Math.round((acceptanceProgress / totalImportantSections) * 100);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Terms of Service</h1>
                <div className="flex items-center space-x-4 text-sm text-gray-600">
                  <span>Last updated: {lastUpdated}</span>
                  <span>•</span>
                  <span>Effective: {effectiveDate}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button className="hidden md:flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors">
                <Download className="w-4 h-4" />
                <span className="font-medium">Download PDF</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sticky top-8">
              <h3 className="font-bold text-gray-900 mb-4">Quick Navigation</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => {
                      document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className={`flex items-center space-x-2 w-full text-left px-3 py-2 text-sm rounded-lg transition-colors ${
                      section.important 
                        ? 'text-gray-900 hover:bg-amber-50 hover:text-amber-700' 
                        : 'text-gray-600 hover:bg-blue-50 hover:text-blue-600'
                    }`}
                  >
                    {section.important && <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                    <span className="truncate">{section.title}</span>
                  </button>
                ))}
              </nav>

              {/* Acceptance Progress */}
              <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold text-blue-900 text-sm">Important Sections</h4>
                  <span className="text-sm font-medium text-blue-700">{acceptanceProgress}/{totalImportantSections}</span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-2 mb-3">
                  <div 
                    className="bg-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${acceptancePercentage}%` }}
                  ></div>
                </div>
                <p className="text-xs text-blue-700">
                  Review all important sections to understand your rights and responsibilities.
                </p>
              </div>

              {/* Quick Actions */}
              <div className="mt-6 space-y-3">
                <button className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-blue-700 transition-colors">
                  <CheckCircle className="w-4 h-4" />
                  <span>Accept All Terms</span>
                </button>
                <button className="w-full flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-200 transition-colors">
                  <Share2 className="w-4 h-4" />
                  <span>Share Terms</span>
                </button>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Summary Card */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-8">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <FileText className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Terms of Service Agreement</h2>
                    <p className="text-blue-100">Please read these terms carefully before using our services</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                      <Clock className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg">Last Updated</h3>
                    <p className="text-blue-100 text-sm">{lastUpdated}</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                      <AlertCircle className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg">{totalImportantSections} Key Sections</h3>
                    <p className="text-blue-100 text-sm">Require your attention</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                      <User className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg">Legal Agreement</h3>
                    <p className="text-blue-100 text-sm">Binding contract between parties</p>
                  </div>
                </div>
              </div>

              {/* Important Notice */}
              <div className="bg-amber-50 border-b border-amber-200 p-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-bold text-amber-900 mb-2">Important Legal Notice</h3>
                    <p className="text-amber-800 text-sm">
                      These Terms of Service govern your use of RideHub's services. By accessing or using our platform, 
                      you acknowledge that you have read, understood, and agree to be bound by these terms. 
                      If you do not agree with any part of these terms, you must not use our services.
                    </p>
                  </div>
                </div>
              </div>

              {/* Terms Sections */}
              <div className="p-8">
                <div className="prose prose-lg max-w-none">
                  {sections.map((section) => (
                    <div key={section.id} id={section.id} className="mb-8 last:mb-0">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 flex-1">
                          {section.important && (
                            <div className="flex items-center space-x-1 bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-semibold mt-1">
                              <AlertCircle className="w-3 h-3" />
                              <span>Important</span>
                            </div>
                          )}
                          <h3 className="text-xl font-bold text-gray-900 flex-1">{section.title}</h3>
                        </div>
                        <button
                          onClick={() => toggleSection(section.id)}
                          className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          {openSections[section.id] ? (
                            <ChevronUp className="w-5 h-5 text-gray-500" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-500" />
                          )}
                        </button>
                      </div>
                      
                      <div className={`mt-4 text-gray-700 leading-relaxed ${openSections[section.id] ? 'block' : 'hidden'}`}>
                        {section.content.split('\n\n').map((paragraph, index) => (
                          <div key={index} className="mb-4 last:mb-0">
                            {paragraph.split('**').map((text, i) => 
                              i % 2 === 1 ? (
                                <strong key={i} className="text-gray-900">{text}</strong>
                              ) : (
                                text
                              )
                            )}
                          </div>
                        ))}
                      </div>

                      {section.important && (
                        <div className="mt-4 flex items-center space-x-4">
                          <button
                            onClick={() => toggleAcceptance(section.id)}
                            className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                              acceptedSections[section.id]
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {acceptedSections[section.id] ? (
                              <CheckCircle className="w-4 h-4" />
                            ) : (
                              <XCircle className="w-4 h-4" />
                            )}
                            <span>
                              {acceptedSections[section.id] ? 'Section Reviewed' : 'Mark as Reviewed'}
                            </span>
                          </button>
                          <span className="text-sm text-gray-500">
                            Click to acknowledge you've read this section
                          </span>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Final Acceptance */}
                  <div className="mt-12 p-6 bg-gray-50 rounded-2xl border border-gray-200">
                    <h3 className="font-bold text-gray-900 mb-4 text-center">Final Acceptance</h3>
                    <div className="text-center mb-6">
                      <div className="inline-flex items-center space-x-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full mb-4">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-semibold">
                          {acceptanceProgress} of {totalImportantSections} important sections reviewed
                        </span>
                      </div>
                      <p className="text-gray-600 mb-4">
                        By proceeding to use RideHub services, you acknowledge that you have read, understood, 
                        and agree to be bound by these Terms of Service.
                      </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                      <button 
                        className="flex items-center justify-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors"
                        disabled={acceptanceProgress < totalImportantSections}
                      >
                        <CheckCircle className="w-5 h-5" />
                        <span>Accept All Terms</span>
                      </button>
                      <button className="flex items-center justify-center space-x-2 bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors">
                        <XCircle className="w-5 h-5" />
                        <span>Decline Terms</span>
                      </button>
                    </div>

                    {acceptanceProgress < totalImportantSections && (
                      <p className="text-center text-amber-600 text-sm mt-4">
                        Please review all {totalImportantSections} important sections before accepting.
                      </p>
                    )}
                  </div>

                  {/* Contact Information */}
                  <div className="mt-8 grid md:grid-cols-2 gap-6">
                    <div className="bg-blue-50 p-6 rounded-2xl border border-blue-200">
                      <h4 className="font-bold text-blue-900 mb-3">Legal Department</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4 text-blue-600" />
                          <span>myjourneycabs@gmail.com</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Phone className="w-4 h-4 text-blue-600" />
                          <span>+91 9728115781</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-green-50 p-6 rounded-2xl border border-green-200">
                      <h4 className="font-bold text-green-900 mb-3">Customer Support</h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4 text-green-600" />
                          <span>myjourneycabs@gmail.com</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Phone className="w-4 h-4 text-green-600" />
                          <span>+91 9728115781</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Reference Cards */}
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Cancellation Policy</h4>
                <p className="text-gray-600 text-sm">
                  Understand cancellation windows, fees, and no-show policies for rides.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
                  <CreditCard className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Payment Terms</h4>
                <p className="text-gray-600 text-sm">
                  Learn about fare calculation, payment methods, and refund procedures.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Safety Guidelines</h4>
                <p className="text-gray-600 text-sm">
                  Review safety protocols and user responsibilities during rides.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;